import { identity } from 'lodash'

import {
  ADD_NOTE,
  EDIT_NOTE,
  EDIT_NOTE_TEMPLATE_ATTRIBUTE,
  DELETE_NOTE,
  ATTACH_CHARACTER_TO_NOTE,
  ATTACH_PLACE_TO_NOTE,
  ATTACH_TAG_TO_NOTE,
  REMOVE_CHARACTER_FROM_NOTE,
  REMOVE_PLACE_FROM_NOTE,
  REMOVE_TAG_FROM_NOTE,
  REMOVE_BOOK_FROM_NOTE,
  ATTACH_BOOK_TO_NOTE,
  LOAD_NOTES,
  LOAD_NOTE,
  BATCH_LOAD_NOTE,
  REMOVE_NOTE,
  DUPLICATE_NOTE,
  EDIT_NOTE_TITLE,
  EDIT_NOTE_CONTENT,
  EDIT_NOTE_CUSTOM_ATTRIBUTE,
  REORDER_NOTE_MANUALLY,
} from '../constants/ActionTypes'
import { note } from '../store/initialState'
import selectors from '../selectors'

/**
 * @typedef {import('../types/plottr').Slate} Slate
 * @typedef {import('../index').Note} Note
 * @typedef {import('../index').TextAttributeData} TextAttributeData
 * @typedef {import('../index').DOMSelection} DOMSelection
 * @typedef {import('../index').SlateSelection} SlateSelection
 * @typedef {import('../index').Dispatch} Dispatch
 * @typedef {import('../index').GetState} GetState
 */

/**
 * @typedef {ReturnType<typeof addNote> | ReturnType<typeof addNoteWithValues> | ReturnType<typeof addNoteWithContent> | ReturnType<typeof editNote> | ReturnType<typeof editNoteTemplateAttribute> | ReturnType<typeof deleteNote> | ReturnType<typeof addCharacter> | ReturnType<typeof addPlace> | ReturnType<typeof addTag> | ReturnType<typeof addBook> | ReturnType<typeof removeCharacter> | ReturnType<typeof removePlace> | ReturnType<typeof removeTag> | ReturnType<typeof removeBook> | ReturnType<typeof load> | ReturnType<typeof loadSingle> | ReturnType<typeof batchLoad> | ReturnType<typeof removeSingle> | ReturnType<typeof duplicateNote> | ReturnType<typeof _reorderNoteManually> | ReturnType<typeof editNoteTitle> | ReturnType<typeof editNoteContent> | ReturnType<typeof editNoteCustomAttribute>} NotesActions
 */

const { visibleSortedNotesByCategorySelector } = selectors(identity)

/**
 * @return {{ type: 'ADD_NOTE', title: String, content: Slate | String }}
 */
export function addNote() {
  return { type: ADD_NOTE, title: note.title, content: note.content }
}

/**
 * @param {String} title
 * @param {Slate | String} content
 * @param {Partial<Note>} restOfProps
 * @return {{ type: 'ADD_NOTE', title: String, content: Slate | String, restOfProps: Partial<Note> }}
 */
export function addNoteWithValues(title, content, restOfProps) {
  return { type: ADD_NOTE, title, content, restOfProps }
}

/**
 * @return {{ type: 'ADD_NOTE' } & Note}
 */
export function addNoteWithContent(noteContent = note) {
  return { type: ADD_NOTE, ...noteContent }
}

/**
 * @typedef NoteUpdateValue
 * @property {Note[keyof Note]} value
 * @property {DOMSelection | SlateSelection} [selection]
 */

/**
 * @typedef {Record<keyof Note, NoteUpdateValue>} UpdateNoteAttributes
 */

/**
 * @param {number} id
 * @param {Partial<UpdateNoteAttributes>} attributes
 * @return {{ type: 'EDIT_NOTE', id: number, attributes: Partial<UpdateNoteAttributes> }}
 */
export function editNote(id, attributes) {
  return { type: EDIT_NOTE, id, attributes }
}

/**
 * @param {number} id
 * @param {String} templateId
 * @param {String} name
 * @param {TextAttributeData} value
 * @param {DOMSelection | SlateSelection} selection
 * @return {{ type: 'EDIT_NOTE_TEMPLATE_ATTRIBUTE', id: number, templateId: String, name: String, value: TextAttributeData, selection: DOMSelection | SlateSelection }}
 */
export function editNoteTemplateAttribute(id, templateId, name, value, selection) {
  return {
    type: EDIT_NOTE_TEMPLATE_ATTRIBUTE,
    id,
    templateId,
    name,
    value,
    selection,
  }
}

/**
 * @param {number} id
 * @return {{ type: 'DELETE_NOTE', id: number }}
 */
export function deleteNote(id) {
  return { type: DELETE_NOTE, id }
}

/**
 * @param {number} id
 * @param {number} characterId
 * @return {{ type: 'ATTACH_CHARACTER_TO_NOTE', id: number, characterId: number }}
 */
export function addCharacter(id, characterId) {
  return { type: ATTACH_CHARACTER_TO_NOTE, id, characterId }
}

/**
 * @param {number} id
 * @param {number} placeId
 * @return {{ type: 'ATTACH_PLACE_TO_NOTE', id: number, placeId: number }}
 */
export function addPlace(id, placeId) {
  return { type: ATTACH_PLACE_TO_NOTE, id, placeId }
}

/**
 * @param {number} id
 * @param {number} tagId
 * @return {{ type: 'ATTACH_TAG_TO_NOTE', id: number, tagId: number }}
 */
export function addTag(id, tagId) {
  return { type: ATTACH_TAG_TO_NOTE, id, tagId }
}

/**
 * @param {number} id
 * @param {number | 'series'} bookId
 * @return {{ type: 'ATTACH_BOOK_TO_NOTE', id: number, bookId: number | 'series' }}
 */
export function addBook(id, bookId) {
  return { type: ATTACH_BOOK_TO_NOTE, id, bookId }
}

/**
 * @param {number} id
 * @param {number} characterId
 * @return {{ type: 'REMOVE_CHARACTER_FROM_NOTE', id: number, characterId: number }}
 */
export function removeCharacter(id, characterId) {
  return { type: REMOVE_CHARACTER_FROM_NOTE, id, characterId }
}

/**
 * @param {number} id
 * @param {number} placeId
 * @return {{ type: 'REMOVE_PLACE_FROM_NOTE', id: number, placeId: number }}
 */
export function removePlace(id, placeId) {
  return { type: REMOVE_PLACE_FROM_NOTE, id, placeId }
}

/**
 * @param {number} id
 * @param {number} tagId
 * @return {{ type: 'REMOVE_TAG_FROM_NOTE', id: number, tagId: number }}
 */
export function removeTag(id, tagId) {
  return { type: REMOVE_TAG_FROM_NOTE, id, tagId }
}

/**
 * @param {number} id
 * @param {number | 'series'} bookId
 * @return {{ type: 'REMOVE_BOOK_FROM_NOTE', id: number, bookId: number | 'series' }}
 */
export function removeBook(id, bookId) {
  return { type: REMOVE_BOOK_FROM_NOTE, id, bookId }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Array<Note>} notes
 * @return {{ type: 'LOAD_NOTES', patching: boolean, notes: Array<Note> }}
 */
export function load(patching, notes) {
  return { type: LOAD_NOTES, patching, notes }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Note} note
 * @return {{ type: 'LOAD_NOTE', patching: boolean, note: Note }}
 */
export function loadSingle(patching, note) {
  return { type: LOAD_NOTE, patching, note }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Array<Note>} notes
 * @return {{ type: 'BATCH_LOAD_NOTE', patching: boolean, notes: Array<Note> }}
 */
export function batchLoad(patching, notes) {
  return { type: BATCH_LOAD_NOTE, patching, notes }
}

// NOTE: "load", "loadSingle", "batchLoad" and "removeSingle" are for
// external sync and not for general use.
/**
 * @param {boolean} patching
 * @param {Note} note
 * @return {{ type: 'REMOVE_NOTE', patching: boolean, note: Note }}
 */
export function removeSingle(patching, note) {
  return { type: REMOVE_NOTE, patching, note }
}

/**
 * @param {number} id
 * @return {{ type: 'DUPLICATE_NOTE', id: number, lastEdited: number }}
 */
export function duplicateNote(id) {
  return { type: DUPLICATE_NOTE, id, lastEdited: new Date().getTime() }
}

/**
 * @param {number} id
 * @param {number} oldPosition
 * @param {number} newPosition
 * @param {String | Null} newCategoryId
 * @param {'up' | 'down'} direction
 * @param {Record<String, Array<Note>>} notesByCategory
 * @return {{ type: 'REORDER_NOTE_MANUALLY', id: number, oldPosition: number, newPosition: number, newCategoryId: String | Null, direction: 'up' | 'down', notesByCategory: Record<String, Array<Note>> }}
 */
export const _reorderNoteManually = (
  id,
  oldPosition,
  newPosition,
  newCategoryId,
  direction,
  notesByCategory
) => {
  return {
    type: REORDER_NOTE_MANUALLY,
    id,
    oldPosition,
    newPosition,
    newCategoryId,
    direction,
    notesByCategory,
  }
}

/**
 * @param {number} noteId
 * @param {number} oldPosition
 * @param {number} newPosition
 * @param {String | Null} newCategoryId
 * @param {'up' | 'down'} direction
 * @return {function(Dispatch, GetState): Void}
 */
export const reorderNotes =
  (noteId, oldPosition, newPosition, newCategoryId, direction) => (dispatch, getState) => {
    const visibleNotesByCategory = visibleSortedNotesByCategorySelector(getState())
    dispatch(
      _reorderNoteManually(
        noteId,
        oldPosition,
        newPosition,
        newCategoryId,
        direction,
        visibleNotesByCategory
      )
    )
  }

/**
 * @param {number} id
 * @param {String} newTitle
 * @param {DOMSelection | Null} selection
 * @return {{ type: 'EDIT_NOTE_TITLE', id: number, newTitle: String, selection: DOMSelection | Null }}
 */
export function editNoteTitle(id, newTitle, selection) {
  return { type: EDIT_NOTE_TITLE, id, newTitle, selection }
}

/**
 * @param {number} id
 * @param {Slate} newContent
 * @param {SlateSelection | Null} selection
 * @return {{ type: 'EDIT_NOTE_CONTENT', id: number, newContent: Slate, selection: SlateSelection | Null }}
 */
export function editNoteContent(id, newContent, selection) {
  return { type: EDIT_NOTE_CONTENT, id, newContent, selection }
}

/**
 * @param {number} id
 * @param {String} name
 * @param {TextAttributeData} newValue
 * @param {DOMSelection | SlateSelection} selection
 * @return {{ type: 'EDIT_NOTE_CUSTOM_ATTRIBUTE', id: number, name: String, newValue: TextAttributeData, selection: DOMSelection | SlateSelection }}
 */
export function editNoteCustomAttribute(id, name, newValue, selection) {
  return { type: EDIT_NOTE_CUSTOM_ATTRIBUTE, id, name, newValue, selection }
}
